// DockedShell — the four docked chrome regions of the AvatarOS shell, per the
// approved "Docked Shell" build spec (aos-a2f/build/Docked Shell - Build Spec.md)
// and design section 2a: left tool rail, collapsible outliner, viewport with a
// bottom content dock, right inspector, and the console strip.
//
// The whole point of this chrome is that NONE of it depends on a character
// being loaded — only the viewport reacts to load state. Every panel here
// renders (with an explicit empty state where applicable) from boot onward.
//
// This file replaces OutlinerRail.jsx, which was these panels' ancestor: a
// single ?tune-gated overlay carrying the gizmo target list, the W/E/R mode
// buttons, and the pose-save affordance. Those jobs split across the shell —
// modes on the tool rail, object list in the outliner, save/dirty in the
// inspector (next to the properties it commits) — and stop being dev chrome:
// they are the app's permanent layout now.
//
// Colors are palette tokens only (ENG-880). Type-badge colors ride the wire
// tokens (category coding, not theme — same rule as node pins).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import DirtyBadge from './DirtyBadge'
import { MONO, UI_FONT } from './PipelineGlyph.jsx'
import { sceneSelection, MODES, SPACES } from '../lib/sceneSelection'
import { getAdapters, modeAllowed } from '../lib/transformAdapters'
import { liveTuneStore } from '../lib/liveTuneStore'
import { shellLayout, COLLAPSED_SIZE, LIMITS } from '../lib/shellLayout.js'
import { consoleTally } from '../lib/consoleTally.js'
import { menubarCommands } from '../lib/menubarCommands.js'
import { activeEnvAssetId, buildBlockedReason } from '../lib/activeEnvironmentAsset.js'
import { bakeSettingsStore } from '../lib/bakeSettingsStore'
import { navSettingsStore } from '../lib/navSettingsStore'
import { assetInspectorHost } from '../lib/assetInspectorHost.js'
import { generatePanelHost } from '../lib/generatePanelHost.js'
import { TYPE_TOKEN } from '../lib/outlinerTree.js'

// Outliner row status-dot tones (see the dot in the row markup below).
const STATUS_TONE = {
  ok: 'var(--m-online)',
  warn: 'var(--m-warn)',
  error: 'var(--m-danger)',
}
// Line-art icons rather than text glyphs: they inherit the rail's colour through
// currentColor, which a default-emoji-presentation glyph like '⚡' never did.
import { SurfaceIcon, FrameIcon, ParallaxIcon, BrainIcon } from './icons/SurfaceIcons.jsx'
import { headTrackedCamera } from '../lib/headTrackedCamera.js'
import { resolveParallaxRailState } from '../lib/headTrackIndicatorState.js'
import { buildTransformFields, applyAxisEdit, editKey } from '../lib/transformFields.js'
import { axisColor } from '../lib/axisColors.js'
import * as history from '../lib/transformHistory'
import { pill, head } from '../lib/studioStyles.js'

const LINE = '1px solid var(--m-line)'
const HOVER_BG = 'color-mix(in srgb, var(--m-fg) 5%, transparent)'
// Selected/active state (Studio): the palette's own accent-soft wash. On the
// legacy palettes accentSoft ≈ the old 18% accent color-mix, so their look
// holds; the Studio pair resolves the designed mint wash.
const ACCENT_WASH = 'var(--m-accent-soft)'

// Section-header type (gass .stu-panel-head via the head tokens).
const label = {
  ...head, whiteSpace: 'nowrap',
}
const mutedMono = { fontFamily: MONO, fontSize: 9, color: 'var(--m-fg-3)', whiteSpace: 'nowrap' }

function useStore(store) {
  const [state, setState] = useState(store.getState())
  useEffect(() => store.subscribe(() => setState({ ...store.getState() })), [store])
  return state
}

// ── Resize handles ────────────────────────────────────────────────────────────

/**
 * 5px drag handle. `axis` 'x' resizes a width, 'y' a height; `invert` flips the
 * delta for handles whose panel grows toward the drag origin (inspector, dock).
 */
function ResizeHandle({ axis, storeKey, invert = false, disabled = false }) {
  const onPointerDown = useCallback((e) => {
    if (disabled) return
    e.preventDefault()
    const start = axis === 'x' ? e.clientX : e.clientY
    const base = shellLayout.getState()[storeKey]
    const move = (ev) => {
      const now = axis === 'x' ? ev.clientX : ev.clientY
      const delta = (now - start) * (invert ? -1 : 1)
      shellLayout.setState({ [storeKey]: base + delta })
    }
    const up = () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
    }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
  }, [axis, storeKey, invert, disabled])

  return (
    <div
      data-testid={`shell-resize-${storeKey}`}
      onPointerDown={onPointerDown}
      style={{
        flex: 'none',
        [axis === 'x' ? 'width' : 'height']: 5,
        background: 'var(--m-line)',
        cursor: disabled ? 'default' : axis === 'x' ? 'col-resize' : 'row-resize',
        touchAction: 'none',
      }}
      onMouseEnter={e => { if (!disabled) e.currentTarget.style.background = 'var(--m-accent)' }}
      onMouseLeave={e => { e.currentTarget.style.background = 'var(--m-line)' }}
    />
  )
}

// ── Tool rail ─────────────────────────────────────────────────────────────────

const MODE_UI = {
  translate: { glyph: '✥', key: 'W', name: 'Move' },
  rotate: { glyph: '↻', key: 'E', name: 'Rotate' },
  scale: { glyph: '⤢', key: 'R', name: 'Scale' },
}
const KEY_TO_MODE = { w: 'translate', e: 'rotate', r: 'scale' }

// ── Button metrics ───────────────────────────────────────────────────────────
// PlayCanvas's left toolbar: 40x40 buttons, 24px glyph, square (no radius, no
// border), and its three-state contrast — dim at rest, bright text on a raised
// background on hover, brightest on a RECESSED background when active
// (sass/editor/_editor-left-toolbar.scss: $text-dark → $text-primary +
// $bcg-dark → $bcg-darkest). Rides --aos-icon-scale so the Display setting
// still resizes it.
function railBtnStyle(enabled, on) {
  return {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: '100%',
    height: 'calc(40px * var(--aos-icon-scale, 1))',
    flex: 'none',
    // Square, deliberately — see the parity note above. The Studio restyle
    // tokenised radii app-wide and swept this one to --m-radius-sm (8px) with
    // it, which the commit did not mention and which contradicts the comment
    // two lines up; tool-rail.spec.js pins it at 0.
    margin: 0, border: 'none', borderRadius: 0, padding: 0,
    // Studio active state: accent-soft wash + accent glyph (gass .stu-rail-btn
    // .active), replacing the PlayCanvas recessed-background mark.
    background: on ? 'var(--m-accent-soft)' : 'transparent',
    color: on ? 'var(--m-accent)' : 'var(--m-fg-3)',
    opacity: enabled ? 1 : 0.3,
    cursor: enabled ? 'pointer' : 'not-allowed',
    fontSize: 'calc(24px * var(--aos-icon-scale, 1))',
    lineHeight: 1,
    transition: 'background var(--m-ease), color var(--m-ease)',
  }
}

/**
 * One rail button. `on` marks a live mode/state; `reason` disables + explains.
 *
 * MODULE SCOPE, and it must stay there. Defined inside ToolRail it was a new
 * component TYPE on every render, so React unmounted and rebuilt every button
 * whenever the rail re-rendered — which App's 1.5s outliner tick guarantees.
 * That reset hover (the buttons visibly blinked) and, worse, destroyed any click
 * whose mousedown/mouseup straddled a rebuild: the buttons simply did nothing.
 */
function RailBtn({ id, glyph, title, reason, on = false, warn = false, onClick }) {
  const enabled = !reason
  // `warn` marks a clickable problem state (RED at rest — Andres wanted
  // blocked to read unmistakably, not amber — reason in the tooltip) —
  // distinct from `reason`, which DISABLES. The parallax button uses it for
  // blocked/error: the viewport deliberately shows no pill for those
  // (headTrackIndicatorState.js), so this mark is the one listed-with-reason
  // surface, and the click is the retry.
  const restColor = warn ? 'var(--m-danger)' : 'var(--m-fg-3)'
  return (
    <button
      type="button"
      data-testid={`shell-tool-${id}`}
      data-active={on ? '1' : '0'}
      data-warn={warn ? '1' : '0'}
      disabled={!enabled}
      onClick={enabled ? onClick : undefined}
      title={reason ? `${title} — ${reason}` : title}
      style={{ ...railBtnStyle(enabled, on), ...(warn && !on ? { color: restColor } : {}) }}
      onMouseEnter={e => {
        if (!enabled || on) return
        e.currentTarget.style.background = 'var(--m-panel-hi)'
        e.currentTarget.style.color = warn ? 'var(--m-danger)' : 'var(--m-fg)'
      }}
      onMouseLeave={e => {
        if (on) return
        e.currentTarget.style.background = 'transparent'
        e.currentTarget.style.color = restColor
      }}
    >{glyph}</button>
  )
}

/** Section rule. Groups read as groups without spending a label's height on a
 *  40px-wide rail — the tooltips carry the naming. */
function RailRule() {
  return <div style={{ height: 1, background: 'var(--m-line)', margin: '5px 7px', flex: 'none' }} />
}

/**
 * 38px fixed icon stack. Every button is a real action: the three gizmo modes
 * (disabled with the reason until a gizmo-drivable object is selected), frame
 * avatar, and the appearance settings. Nothing decorative.
 *
 * Also owns the transform hotkeys (W/E/R + undo/redo), ported verbatim from
 * OutlinerRail — the rail is the one shell panel that can never be collapsed
 * away, so the keyboard contract lives with it.
 */
export function ToolRail({ onOpenAppearance, onOpenConversation, onOpenVersions }) {
  const [, force] = useState(0)
  useEffect(() => sceneSelection.subscribe(() => force(n => n + 1)), [])
  useEffect(() => history.subscribe(() => force(n => n + 1)), [])
  // Adapter AVAILABILITY lives in these stores (env splat present, torch armed,
  // avatar mounted) — the guard below can only observe a change if it re-renders
  // on one. Ported from OutlinerRail along with the guard itself.
  useEffect(() => liveTuneStore.subscribe(() => force(n => n + 1)), [])
  // The graph buttons and their active marks come from the menubar registry —
  // re-render when the editor publishes or withdraws them.
  useEffect(() => menubarCommands.subscribe(() => force(n => n + 1)), [])
  // Parallax view-mode button: its `on` state mirrors the head-track runtime,
  // which notifies on every state transition (no polling).
  useEffect(() => headTrackedCamera.onStateChange(() => force(n => n + 1)), [])

  const { selectedId, mode } = sceneSelection.getState()
  const selected = getAdapters().find(a => a.id === selectedId) ?? null

  // Selected object left the scene (environment swapped away, avatar unmounted,
  // torch handed to the headlamp — or a select() call named an object that was
  // never available) — drop the selection rather than leaving a gizmo pointing
  // at nothing. Availability is in the deps because adapter identities are
  // cached and stable: keying only on `selected`/`selectedId` meant an object
  // that went away UNDER a live selection never cleared.
  const selectedAvailable = selected ? selected.isAvailable() : true
  useEffect(() => {
    if (selected && !selectedAvailable) sceneSelection.clear()
  }, [selected, selectedId, selectedAvailable])

  // Swapping the environment swaps the OBJECT the undo stack describes. Its
  // entries are poses of the previous splat, so undoing into them would apply
  // the old environment's placement to the new one. Drop them. (From
  // OutlinerRail, ENG-895.)
  const splatUrl = liveTuneStore.getState().environment.splatUrl
  useEffect(() => { history.clear('environment') }, [splatUrl])

  // ── keyboard: W/E/R + undo/redo (from OutlinerRail, ENG-895) ──────────────
  useEffect(() => {
    // TEXT-ENTRY targets only. The scale slider is an input[type=range] with no
    // native undo, so range inputs are NOT excluded; text fields have native
    // undo and must keep it.
    const TEXT_ENTRY_TYPES = new Set(['text', 'number', 'search', 'email', 'url', 'tel', 'password'])
    const isTextEntry = (t) => {
      if (!t) return false
      if (t.isContentEditable || t.tagName === 'TEXTAREA') return true
      if (t.tagName !== 'INPUT') return false
      return TEXT_ENTRY_TYPES.has((t.type || 'text').toLowerCase())
    }
    const isUndoKey = (e) =>
      (e.ctrlKey || e.metaKey) && !e.shiftKey &&
      String(e.key).toLowerCase() === 'z' && !isTextEntry(e.target)

    const onKey = (e) => {
      const sel = sceneSelection.getState()
      // Undo/redo targets the selection when there is one, else the most
      // recently edited object (tune-panel pose fields write the same stack).
      const id = sel.selectedId ?? history.lastEditedId()
      if (!id) return
      const a = getAdapters().find(x => x.id === id)
      if (!a) return

      // Capture-phase + stopPropagation so the blueprint graph's document-level
      // handler doesn't ALSO undo a node edit from the same keystroke. Only
      // claimed when this object's stack has something to undo.
      const redoKey = (e.ctrlKey || e.metaKey) &&
        (String(e.key).toLowerCase() === 'y' || (String(e.key).toLowerCase() === 'z' && e.shiftKey))

      if (isUndoKey(e) && history.canUndo(id)) {
        const restored = history.undo(id, a.read())
        if (restored) { e.preventDefault(); e.stopPropagation(); a.write(restored) }
        return
      }
      if (redoKey && !isTextEntry(e.target) && history.canRedo(id)) {
        const restored = history.redo(id, a.read())
        if (restored) { e.preventDefault(); e.stopPropagation(); a.write(restored) }
        return
      }

      // W/E/R needs a real selection — there's no gizmo to re-mode otherwise.
      if (!sel.selectedId) return
      if (e.ctrlKey || e.metaKey || e.altKey || isTextEntry(e.target)) return
      const next = KEY_TO_MODE[String(e.key).toLowerCase()]
      if (next && modeAllowed(a.caps, next)) {
        e.preventDefault()
        sceneSelection.setState({ mode: next })
      }
    }

    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
  }, [])

  // Editor surfaces + build targets, resolved the same way the menubar resolves
  // them (menubarCommands / activeEnvironmentAsset) so the rail and the menus can
  // never disagree about what is available.
  const editorsReady = menubarCommands.has('editors.open')
  const activeBP = menubarCommands.stateOf('activeBP', null)
  const openSurface = (id) => menubarCommands.run('editors.open', id)

  // Parallax view-mode button state — shared decision module with the pill
  // (headTrackIndicatorState.js), where blocked/error live as this button's
  // warn mark + tooltip rather than a permanent viewport banner.
  const ht = resolveParallaxRailState({ state: headTrackedCamera.state, errorMessage: headTrackedCamera.errorMessage })
  const noEditor = editorsReady ? null : 'blueprint editor not open'
  const noEnv = buildBlockedReason()

  return (
    <div
      data-testid="shell-tool-rail"
      style={{
        flex: 'none', width: 'calc(40px * var(--aos-icon-scale, 1))', background: 'var(--m-panel)',
        borderRight: LINE, display: 'flex', flexDirection: 'column',
        alignItems: 'stretch', padding: 0, gap: 0, userSelect: 'none',
        overflowY: 'auto', overflowX: 'hidden',
      }}
    >
      {/* ── Transform ── */}
      {MODES.map(m => {
        const { glyph, key, name } = MODE_UI[m]
        const enabled = selected ? modeAllowed(selected.caps, m) : false
        return (
          <RailBtn
            key={m}
            id={m}
            glyph={glyph}
            title={`${name} (${key})`}
            reason={enabled ? null : 'select a movable object in the outliner first'}
            on={enabled && mode === m}
            onClick={() => sceneSelection.setState({ mode: m })}
          />
        )
      })}

      <RailRule />
      {/* ── View ── */}
      <RailBtn id="frame" glyph={<FrameIcon />} title="Frame avatar (F)" onClick={() => window.__FRAME_AVATAR__?.()} />

      <RailRule />
      {/* ── Graphs ── the three blueprint editors, one click each rather than
          two levels of menu. Same registry command the menus use. */}
      <RailBtn
        id="graph-body" glyph={<SurfaceIcon surface="body" />} title="Body Blueprint"
        reason={noEditor} on={activeBP === 'body'}
        onClick={() => openSurface('body')}
      />
      <RailBtn
        id="graph-face" glyph={<SurfaceIcon surface="face" />} title="Face Blueprint"
        reason={noEditor} on={activeBP === 'face'}
        onClick={() => openSurface('face')}
      />
      <RailBtn
        id="graph-events" glyph={<SurfaceIcon surface="events" />} title="Event Graph"
        reason={noEditor} on={activeBP === 'events'}
        onClick={() => openSurface('events')}
      />

      <RailRule />
      {/* ── Brain ── the conversation workspace (convorcher: which brain the
          character runs on, stories, cached responses, prefab triggers). Same
          panel the Character menu opens — one workspace, two doors, exactly the
          Settings precedent at the bottom of this rail. */}
      <RailBtn
        id="brain" glyph={<BrainIcon />} title="Brain (Conversation)"
        reason={onOpenConversation ? null : 'load a character first'}
        onClick={() => onOpenConversation?.()}
      />

      <RailRule />
      {/* ── Version control ── checkpoints, branches, item history. It only
          had a door inside the assets dock (the ⏇ toolbar button), so it could
          not be opened at all with the dock collapsed. Same two-doors-one-panel
          shape as Brain and Settings; the open flag lives in versionPanelStore
          so neither door owns it. */}
      <RailBtn
        id="version-control" glyph="⏇" title="Version Control"
        reason={onOpenVersions ? null : 'load a character first'}
        onClick={() => onOpenVersions?.()}
      />

      <RailRule />
      {/* ── Build ── bakes against the ACTIVE environment, so both disable with
          the reason when there isn't one (same rule as the Build menu). */}
      <RailBtn
        id="bake-lighting" glyph="☀" title="Bake Lighting"
        reason={noEnv}
        onClick={() => { const id = activeEnvAssetId(); if (id) bakeSettingsStore.open(id) }}
      />
      <RailBtn
        id="build-navmesh" glyph="⬚" title="Build Navmesh"
        reason={noEnv}
        onClick={() => { const id = activeEnvAssetId(); if (id) navSettingsStore.open(id) }}
      />

      <RailRule />
      {/* ── Parallax view mode (ENG-981) ── own section, per Andres. One click
          turns head-tracked 3D parallax on with the tuned preset
          (VIEW_MODE_PRESET), like a viewport perspective/ortho toggle. Works
          in every view: the rig camera via apply(), the editor camera via
          EditorHeadParallax. The click IS the user gesture the
          camera-permission prompt rides on — same contract as the viewport
          pill, the only other start() caller. While blocked/errored the
          button is the warn surface (amber + reason in the tooltip; click
          retries) — the viewport deliberately shows no pill for those. */}
      <RailBtn
        id="parallax" glyph={<ParallaxIcon blocked={ht.warn} />}
        title={ht.title}
        on={ht.on}
        warn={ht.warn}
        onClick={() => headTrackedCamera[ht.action]()}
      />

      {/* Pinned bottom group — PlayCanvas's `push-top { margin-top: auto }`, on a
          slightly recessed band so it reads as chrome rather than another tool. */}
      <span style={{ flex: 1, minHeight: 8 }} />
      <div style={{ background: 'var(--m-bg)', borderTop: LINE, flex: 'none' }}>
        <RailBtn id="settings" glyph="⚙" title="Settings" onClick={() => onOpenAppearance?.()} />
      </div>
    </div>
  )
}

// ── Outliner ──────────────────────────────────────────────────────────────────

/**
 * The hierarchy panel. Rows come from buildOutlinerRows (real scene data);
 * clicking selects — adapter rows additionally arm the transform gizmo, exactly
 * as the old OutlinerRail rows did. Collapses to a 34px vertical strip (⌥O).
 */
export function OutlinerPanel({ rows, selectedRowId, onSelect, onOpenSurface, onToggleVisibility }) {
  const layout = useStore(shellLayout)
  const [query, setQuery] = useState('')
  // Collapsed subtrees (Unity/Unreal-style carets). Session state: reopening
  // the app should show the full hierarchy, not a remembered fold pattern.
  // The mesh list starts folded — ten rows of LOD meshes is detail on demand.
  const [folded, setFolded] = useState(() => new Set(['meshes']))

  // ⌥O toggles, matching the strip's hint. Registered here (not the rail) so
  // the hint and the handler live and die together.
  useEffect(() => {
    const onKey = (e) => {
      if (e.altKey && !e.ctrlKey && !e.metaKey && (e.code === 'KeyO')) {
        e.preventDefault()
        shellLayout.toggleOutliner()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  const hasChildren = useMemo(() => {
    const set = new Set()
    for (const r of rows) if (r.parentId) set.add(r.parentId)
    return set
  }, [rows])

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase()
    // A search overrides folding — a match hidden inside a folded group would
    // read as "not found".
    if (q) return rows.filter(r => r.label.toLowerCase().includes(q))
    const byId = new Map(rows.map(r => [r.id, r]))
    const hidden = (r) => {
      for (let p = r.parentId; p; p = byId.get(p)?.parentId ?? null) {
        if (folded.has(p)) return true
      }
      return false
    }
    return rows.filter(r => !hidden(r))
  }, [rows, query, folded])

  const toggleFold = (id) => setFolded(prev => {
    const next = new Set(prev)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    return next
  })

  const count = rows.length ? `${rows.length} nodes` : '—'

  if (!layout.outlinerOpen) {
    return (
      <div
        data-testid="shell-outliner"
        data-collapsed="1"
        onClick={() => shellLayout.toggleOutliner()}
        title="Expand outliner (⌥O)"
        style={{
          flex: 'none', width: COLLAPSED_SIZE, background: 'var(--m-panel)',
          borderRight: LINE, cursor: 'pointer', userSelect: 'none',
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          gap: 10, padding: '8px 0',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = 'color-mix(in srgb, var(--m-fg) 4%, var(--m-panel))' }}
        onMouseLeave={e => { e.currentTarget.style.background = 'var(--m-panel)' }}
      >
        <span style={{ color: 'var(--m-fg)', fontSize: 10 }}>▸</span>
        <span style={{ ...label, writingMode: 'vertical-rl' }}>OUTLINER</span>
        <span style={{ ...mutedMono, writingMode: 'vertical-rl' }}>{count}</span>
        <span style={{ flex: 1 }} />
        <span style={mutedMono}>⌥O</span>
      </div>
    )
  }

  return (
    <div
      data-testid="shell-outliner"
      data-collapsed="0"
      style={{
        flex: 'none', width: layout.outlinerW, background: 'var(--m-panel)',
        borderRight: LINE, display: 'flex', flexDirection: 'column',
        minWidth: 0, overflow: 'hidden', userSelect: 'none',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', borderBottom: LINE }}>
        <span
          onClick={() => shellLayout.toggleOutliner()}
          title="Collapse outliner (⌥O)"
          style={{ ...label, cursor: 'pointer' }}
        >◂ OUTLINER</span>
        <span style={{ flex: 1 }} />
        <span style={mutedMono}>{count}</span>
      </div>

      <div style={{
        display: 'flex', alignItems: 'center', gap: 7, margin: '7px 8px 6px',
        padding: '4px 10px', background: 'var(--m-panel-hi)',
        border: '1px solid var(--m-line-hi)', borderRadius: 999,
      }}>
        <span style={{ color: 'var(--m-fg-3)', fontSize: 10 }}>⌕</span>
        <input
          data-testid="shell-outliner-search"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search hierarchy…"
          style={{
            flex: 1, minWidth: 0, background: 'transparent', border: 'none', outline: 'none',
            fontFamily: UI_FONT, fontSize: 9.5, color: 'var(--m-fg)',
          }}
        />
      </div>

      <div className="aos-thin-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingBottom: 8 }}>
        {visible.length === 0 && (
          <div style={{ ...mutedMono, whiteSpace: 'normal', padding: '6px 10px', lineHeight: 1.5 }}>
            {rows.length === 0
              ? 'No character loaded — the hierarchy appears when one opens.'
              : 'Nothing matches the search.'}
          </div>
        )}
        {visible.map(r => {
          const on = r.id === selectedRowId
          const foldable = hasChildren.has(r.id)
          return (
            <div
              key={r.id}
              data-testid={`shell-outliner-row-${r.id}`}
              data-selected={on ? '1' : '0'}
              onClick={() => onSelect(on ? null : r.id)}
              onDoubleClick={r.openSurface ? () => onOpenSurface?.(r.openSurface) : undefined}
              title={r.openSurface ? 'Double-click to open the editor' : undefined}
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: `3.5px 9px 3.5px ${4 + r.depth * 12}px`,
                margin: '0 4px', borderRadius: 'var(--m-radius-sm)',
                cursor: 'pointer',
                background: on ? ACCENT_WASH : 'transparent',
                transition: 'background var(--m-ease)',
              }}
              onMouseEnter={e => { if (!on) e.currentTarget.style.background = HOVER_BG }}
              onMouseLeave={e => { e.currentTarget.style.background = on ? ACCENT_WASH : 'transparent' }}
            >
              {/* Caret gutter: rows with children get a fold toggle; leaves get
                  the same width so labels keep one left edge per depth. The
                  clickable box is 20×20 (a real hit target — the old 10×13 at
                  font-size 8 was barely clickable) but contributes only ~12px
                  of row height via the negative vertical margin, keeping the
                  dense DCC row rhythm. */}
              <span
                data-testid={foldable ? `shell-outliner-fold-${r.id}` : undefined}
                onClick={foldable ? (e) => { e.stopPropagation(); toggleFold(r.id) } : undefined}
                onMouseEnter={foldable ? (e) => {
                  e.currentTarget.style.background = 'color-mix(in srgb, var(--m-fg) 10%, transparent)'
                  e.currentTarget.style.color = 'var(--m-fg)'
                } : undefined}
                onMouseLeave={foldable ? (e) => {
                  e.currentTarget.style.background = 'transparent'
                  e.currentTarget.style.color = 'var(--m-fg-3)'
                } : undefined}
                style={{
                  width: 20, height: 20, margin: '-4px 0', flex: 'none',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, borderRadius: 3,
                  color: 'var(--m-fg-3)', cursor: foldable ? 'pointer' : 'default',
                }}
              >{foldable ? (folded.has(r.id) ? '▸' : '▾') : ''}</span>
              <span style={{
                fontSize: 9.5, width: 11, flex: 'none', textAlign: 'center',
                color: on ? 'var(--m-accent)' : 'var(--m-fg-3)',
              }}>{r.glyph}</span>
              <span style={{
                fontFamily: UI_FONT, fontSize: 9.5, flex: 1, minWidth: 0,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                // Selected = accent text on the accent-soft wash (gass
                // .stu-tree-row.active), not a brightness bump.
                color: on ? 'var(--m-accent)' : 'var(--m-fg-2)',
              }}>{r.label}</span>
              {r.dirty && r.dirty !== 'clean' && <DirtyBadge state={r.dirty} size={7} />}
              {/* Status dot — for rows whose whole point is a yes/no the user
                  needs at a glance (today: NAVMESH, green when the scene is
                  navigable). Generic on purpose: `status` is a row field, so the
                  next such row needs no change here. The title carries the
                  reason, so the dot is never a colour with no explanation. */}
              {r.status && (
                <span
                  data-testid={`shell-outliner-status-${r.id}`}
                  data-tone={r.status.tone}
                  title={r.status.title}
                  style={{
                    width: 6, height: 6, borderRadius: '50%', flex: 'none',
                    background: STATUS_TONE[r.status.tone] ?? 'var(--m-fg-3)',
                  }}
                />
              )}
              {r.meta && <span style={{ ...mutedMono, fontSize: 8.5, flex: 'none' }}>{r.meta}</span>}
              {/* Visibility eye — only for rows that DECLARE a visible flag
                  (today: the ground plane, whose `visible` mirrors
                  liveTuneStore.backdrop.groundEnabled). Same 20×20-in-a-dense-
                  row trick as the fold caret; stopPropagation so toggling
                  visibility never doubles as a row selection. */}
              {typeof r.visible === 'boolean' && onToggleVisibility && (
                <span
                  data-testid={`shell-outliner-vis-${r.id}`}
                  title={r.visible ? 'Visible — click to hide' : 'Hidden — click to show'}
                  onClick={(e) => { e.stopPropagation(); onToggleVisibility(r.id) }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'color-mix(in srgb, var(--m-fg) 10%, transparent)'
                    e.currentTarget.style.color = 'var(--m-fg)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent'
                    e.currentTarget.style.color = r.visible ? 'var(--m-fg-2)' : 'var(--m-fg-3)'
                  }}
                  style={{
                    width: 20, height: 20, margin: '-4px -4px -4px 0', flex: 'none',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 11, borderRadius: 3, cursor: 'pointer',
                    color: r.visible ? 'var(--m-fg-2)' : 'var(--m-fg-3)',
                  }}
                >{r.visible ? '◉' : '○'}</span>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}


// ── Transform fields ──────────────────────────────────────────────────────────

/**
 * One axis entry. Committed on blur and on Enter, NOT on every keystroke:
 * a partially-typed number ("-", "0.", "1e") must not be written to the scene,
 * and a live-committing field fights you as you type. Escape reverts.
 *
 * The value is re-read from props whenever the store changes (keyed below), so
 * dragging the gizmo updates the numbers and typing updates the gizmo — one
 * transform, two views, exactly as Unreal/Unity behave.
 */
function AxisField({ axis, value, enabled, reason, step, onCommit }) {
  const inputRef = useRef(null)
  const [draft, setDraft] = useState(value)

  // The field follows the scene UNLESS it currently has focus — then the user
  // owns the text. Gating on `document.activeElement` rather than an `editing`
  // flag is deliberate: a flag only clears on blur, so a field typed into and
  // then abandoned (selection switched, panel collapsed) kept showing its stale
  // draft against a different object's values. Focus is the real question.
  useEffect(() => {
    if (inputRef.current !== document.activeElement) setDraft(value)
  }, [value])

  const commit = () => {
    if (draft === value) return
    const ok = onCommit(draft)
    // Rejected (unparseable / clamped / unchanged) → snap back to the truth
    // rather than leaving a number on screen that isn't in the scene.
    if (!ok) setDraft(value)
  }

  return (
    <span
      title={reason ?? axis.toUpperCase()}
      style={{
        // flex-basis 0 + minWidth 0 is what actually lets three number fields
        // share a 260px panel: a bare `flex:1` leaves the input's intrinsic
        // preferred width as the floor, and the third field overflowed the
        // panel edge.
        flex: '1 1 0', minWidth: 0,
        display: 'flex', alignItems: 'center',
        background: enabled ? 'var(--m-bg)' : 'transparent',
        border: '1px solid var(--m-line)',
        borderLeft: `2px solid ${enabled ? axisColor(axis) : 'var(--m-line-hi)'}`,
        borderRadius: 'var(--m-radius-sm)', overflow: 'hidden',
        opacity: enabled ? 1 : 0.45,
      }}
    >
      <input
        ref={inputRef}
        data-testid={`shell-transform-${axis}`}
        type="number"
        step={step}
        value={draft}
        disabled={!enabled}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => {
          if (e.key === 'Enter') { e.preventDefault(); commit(); e.currentTarget.blur() }
          if (e.key === 'Escape') { e.preventDefault(); setDraft(value); e.currentTarget.blur() }
          // The rail owns W/E/R and Ctrl+Z globally; inside a number field those
          // are text, so keep them here.
          e.stopPropagation()
        }}
        style={{
          // Spinners are ~17px per field — three of them is a whole field's
          // worth of width on a panel this narrow, and a 1px-step spinner is
          // useless for metres anyway (drag the gizmo instead).
          appearance: 'textfield', MozAppearance: 'textfield', WebkitAppearance: 'textfield',
          flex: '1 1 0', width: 0, minWidth: 0, boxSizing: 'border-box',
          background: 'transparent', border: 'none', outline: 'none',
          padding: '3px 2px 3px 4px',
          fontFamily: MONO, fontSize: 9.5,
          color: enabled ? 'var(--m-fg)' : 'var(--m-fg-3)',
        }}
      />
      {/* Axis letter as a suffix, PlayCanvas-style: it names the field without
          costing a label column the panel cannot spare. */}
      <span aria-hidden="true" style={{
        flex: 'none', padding: '0 4px 0 1px', fontFamily: MONO, fontSize: 8,
        color: enabled ? 'var(--m-fg-3)' : 'var(--m-line-hi)',
      }}>{axis.toUpperCase()}</span>
    </span>
  )
}

/**
 * Location / Rotation / Scale, per Unreal's Details panel. Edits go through the
 * SAME adapter.write() the gizmo uses, so undo, the dirty badge and "save pose
 * as default" all behave identically whether you dragged or typed.
 */
function TransformSection({ adapter }) {
  const [, force] = useState(0)
  // Re-read on every store change so gizmo drags show up in the numbers.
  useEffect(() => liveTuneStore.subscribe(() => force(n => n + 1)), [])
  useEffect(() => history.subscribe(() => force(n => n + 1)), [])
  useEffect(() => sceneSelection.subscribe(() => force(n => n + 1)), [])

  const trs = adapter.read()
  const rows = buildTransformFields(trs, adapter.caps)

  const commitAxis = (mode, axis) => (typed) => {
    const before = adapter.read()
    const next = applyAxisEdit({ trs: before, caps: adapter.caps, mode, axis, typed })
    if (!next) return false
    // One undo entry per axis run (recordEdit coalesces consecutive edits of the
    // same key), matching how one gizmo drag is one Ctrl+Z.
    history.recordEdit(adapter.id, before, editKey(mode, axis))
    adapter.write(next)
    return true
  }

  return (
    <div data-testid="shell-transform" style={{ display: 'grid', gap: 5, minWidth: 0 }}>
      {rows.map(row => (
        <div key={row.mode} style={{ display: 'grid', gap: 2, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
            <span style={{ fontFamily: UI_FONT, fontSize: 9, color: 'var(--m-fg-2)' }}>{row.label}</span>
            <span style={{ ...mutedMono, fontSize: 8.5 }}>{row.unit}</span>
            {/* The preserve-ratio lock. Forced on for a uniform-scale object —
                its storage is a single factor, so an unlocked state would be a
                promise it cannot keep (transformFields.js). */}
            {row.locked && (
              <span data-testid="shell-transform-lock" title="Uniform — all axes scale together" style={{ ...mutedMono, fontSize: 8.5 }}>🔒</span>
            )}
            <span style={{ flex: 1 }} />
            {/* The reason is a tooltip on a short chip, not a sentence: at this
                panel width the full text pushed the row past the panel edge. */}
            {row.disabledReason && (
              <span title={row.disabledReason} style={{ ...mutedMono, fontSize: 8, flex: 'none' }}>locked</span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 3, minWidth: 0 }}>
            {row.axes.map(a => (
              <AxisField
                key={a.axis}
                axis={a.axis}
                value={a.value}
                enabled={a.enabled}
                reason={a.reason}
                step={row.step}
                onCommit={commitAxis(row.mode, a.axis)}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

// ── Inspector ─────────────────────────────────────────────────────────────────

/**
 * Property list for the outliner selection. Never collapses, and renders an
 * explicit "nothing selected" state rather than hiding (build spec).
 *
 * For gizmo-drivable selections it also carries the transform controls that
 * aren't modes: world/local space, and the pose save with its dirty readout —
 * the save sits next to the properties it commits (UX proximity rule, the same
 * reason OutlinerRail carried it before this panel existed).
 */
export function InspectorPanel({ row, characterLabel, onOpenSurface, onOpenRigPreview, onRowControl, actionNote }) {
  const layout = useStore(shellLayout)
  const [, force] = useState(0)
  const [save, setSave] = useState({ status: 'idle', msg: null })
  useEffect(() => sceneSelection.subscribe(() => force(n => n + 1)), [])

  // Content-browser asset selection portals its inspector INTO this panel —
  // one details surface, fed by whichever selection happened last (see
  // assetInspectorHost.js). The target div must exist whether or not an asset
  // is active, so the portal has somewhere to land the moment one is picked.
  const [assetActive, setAssetActive] = useState(assetInspectorHost.isActive())
  useEffect(() => assetInspectorHost.subscribe(() => setAssetActive(assetInspectorHost.isActive())), [])
  const hostRef = useCallback((el) => { assetInspectorHost.registerTarget(el) }, [])

  // ── Open only when there is something to inspect ───────────────────────────
  // Nothing selected → the panel IS the collapsed strip. Not "gets collapsed":
  // openness is DERIVED, so the rule holds unconditionally — on boot, after a
  // reload, after a character switch, and however the persisted preference was
  // left. An earlier version wrote inspectorOpen on selection transitions and so
  // left a full-width "NOTHING SELECTED" panel standing whenever the last
  // session ended with one open.
  //
  // `inspectorOpen` keeps its meaning as the user's preference for when a
  // selection DOES exist: collapse it with a selection held and it stays
  // collapsed through the next ones; expand it and selections open it again.
  const hasSelection = !!row || assetActive
  const open = layout.inspectorOpen && hasSelection

  // ⌥I toggles, mirroring the outliner's ⌥O.
  useEffect(() => {
    const onKey = (e) => {
      if (e.altKey && !e.ctrlKey && !e.metaKey && (e.code === 'KeyI')) {
        e.preventDefault()
        shellLayout.toggleInspector()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  // A save that succeeded stops being interesting once the selection changes.
  useEffect(() => { setSave({ status: 'idle', msg: null }) }, [row?.id])

  const { space } = sceneSelection.getState()
  const adapter = row?.adapterId ? getAdapters().find(a => a.id === row.adapterId) : null
  const dirtyState = adapter?.dirty?.() ?? 'clean'
  const canSave = adapter?.canSave?.() ?? false

  const doSave = useCallback(async () => {
    if (!adapter?.save) return
    setSave({ status: 'saving', msg: null })
    try {
      await adapter.save()
      setSave({ status: 'saved', msg: 'saved as default' })
    } catch (err) {
      // Loud failure — never report a save that didn't happen.
      setSave({ status: 'error', msg: String(err?.message ?? err) })
    }
  }, [adapter])

  const type = row?.type ?? 'EMPTY'
  const tone = `var(${TYPE_TOKEN[type] ?? '--m-fg-3'})`

  if (!open) {
    // With a selection the strip is an expand control. With NOTHING selected it
    // is inert on purpose: offering to expand into a panel that would only say
    // "nothing selected" is a dead control, and a hover/pointer that promises
    // something is worse than one that doesn't.
    return (
      <div
        data-testid="shell-inspector"
        data-collapsed="1"
        data-inert={hasSelection ? '0' : '1'}
        onClick={hasSelection ? () => shellLayout.toggleInspector() : undefined}
        title={hasSelection ? 'Expand inspector (⌥I)' : 'Nothing selected — pick a node or an asset'}
        style={{
          flex: 'none', width: COLLAPSED_SIZE, background: 'var(--m-panel)',
          borderLeft: LINE, cursor: hasSelection ? 'pointer' : 'default', userSelect: 'none',
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          gap: 10, padding: '8px 0',
        }}
        onMouseEnter={e => { if (hasSelection) e.currentTarget.style.background = 'color-mix(in srgb, var(--m-fg) 4%, var(--m-panel))' }}
        onMouseLeave={e => { e.currentTarget.style.background = 'var(--m-panel)' }}
      >
        <span style={{ color: hasSelection ? 'var(--m-fg)' : 'var(--m-fg-3)', fontSize: 10 }}>◂</span>
        <span style={{
          ...label, writingMode: 'vertical-rl',
          color: hasSelection ? 'var(--m-fg)' : 'var(--m-fg-3)',
        }}>INSPECTOR</span>
        <span style={{ ...mutedMono, writingMode: 'vertical-rl' }}>{row ? row.label : '—'}</span>
        <span style={{ flex: 1 }} />
        {hasSelection && <span style={mutedMono}>⌥I</span>}
      </div>
    )
  }

  return (
    <div
      data-testid="shell-inspector"
      data-collapsed="0"
      style={{
        flex: 'none', width: layout.inspectorW, background: 'var(--m-panel)',
        display: 'flex', flexDirection: 'column', minWidth: 0, userSelect: 'none',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', borderBottom: LINE }}>
        <span
          onClick={() => shellLayout.toggleInspector()}
          title="Collapse inspector (⌥I)"
          style={{ ...label, cursor: 'pointer' }}
        >INSPECTOR ▸</span>
        <span style={{ flex: 1 }} />
        <span
          data-testid="shell-inspector-type"
          style={{
            fontFamily: UI_FONT, fontSize: 8.5, letterSpacing: '.10em',
            padding: '2.5px 8px', borderRadius: 999, whiteSpace: 'nowrap',
            color: assetActive ? 'var(--m-accent)' : tone,
            border: '1px solid var(--m-line-hi)',
            background: (assetActive || row) ? 'color-mix(in srgb, var(--m-fg) 4%, transparent)' : 'transparent',
          }}
        >{assetActive ? 'ASSET' : type}</span>
      </div>

      {/* Portal target for the content-browser asset inspector. Always mounted;
          fills the panel while an asset is selected. */}
      <div
        ref={hostRef}
        data-testid="shell-inspector-asset-host"
        className="aos-thin-scroll"
        style={{ flex: assetActive ? 1 : 'none', minHeight: 0, overflowY: 'auto', display: assetActive ? 'flex' : 'none', flexDirection: 'column' }}
      />
      {!assetActive && (
      <>

      <div style={{ ...mutedMono, letterSpacing: 'var(--m-head-ls)', padding: '8px 10px 4px', textTransform: 'uppercase' }}>
        {row ? `${row.label}${characterLabel ? ` · ${characterLabel}` : ''}` : 'NOTHING SELECTED'}
      </div>

      <div className="aos-thin-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '2px 10px 10px', display: 'grid', gap: 1, alignContent: 'start' }}>
        {/* TRANSFORM FIRST. Both references put it at the top of the details
            panel — PlayCanvas' ENTITY panel opens with Position/Rotation/Scale,
            Unreal's with the Transform category — because it is the property
            you reach for most and the one the viewport gizmo mirrors. The
            read-only readout below it is reference, not workflow. */}
        {adapter && (
          <div style={{ paddingBottom: 8, marginBottom: 6, borderBottom: LINE, minWidth: 0 }}>
            <div style={{ ...mutedMono, letterSpacing: 'var(--m-head-ls)', paddingBottom: 5 }}>TRANSFORM</div>
            <TransformSection key={adapter.id} adapter={adapter} />

            {/* Gizmo space — belongs with the fields it re-frames. */}
            <div style={{ display: 'flex', gap: 4, marginTop: 7 }}>
              {SPACES.map(sp => (
                <button
                  key={sp}
                  type="button"
                  onClick={() => sceneSelection.setState({ space: sp })}
                  style={{
                    flex: 1, fontSize: 9.5, padding: '3px 0', textTransform: 'uppercase',
                    fontFamily: UI_FONT, ...pill, cursor: 'pointer',
                    background: space === sp ? ACCENT_WASH : 'var(--m-panel-hi)',
                    border: `1px solid ${space === sp ? 'var(--m-accent)' : 'var(--m-line-hi)'}`,
                    color: space === sp ? 'var(--m-accent)' : 'var(--m-fg-2)',
                  }}
                >{sp}</button>
              ))}
            </div>

            {/* Save sits with the pose it commits (UX proximity rule). */}
            {canSave && (
              <>
                <button
                  type="button"
                  onClick={doSave}
                  disabled={save.status === 'saving' || dirtyState === 'clean'}
                  title="Persist this pose to the environment's default_pose on the server"
                  style={{
                    width: '100%', marginTop: 6, padding: '4px 0', fontSize: 10,
                    ...pill, fontFamily: UI_FONT, cursor: dirtyState === 'clean' ? 'default' : 'pointer',
                    border: `1px solid ${dirtyState === 'clean' ? 'var(--m-line-hi)' : 'var(--m-accent)'}`,
                    background: dirtyState === 'clean' ? 'var(--m-panel-hi)' : ACCENT_WASH,
                    color: dirtyState === 'clean' ? 'var(--m-fg-3)' : 'var(--m-accent)',
                  }}
                >
                  {save.status === 'saving' ? 'saving…' : 'Save pose as default'}
                </button>
                <div style={{
                  fontSize: 9, marginTop: 4, fontFamily: UI_FONT,
                  color: save.status === 'error' ? 'var(--m-danger)'
                    : dirtyState === 'dirty' ? 'var(--m-warn)' : 'var(--m-fg-3)',
                }}>
                  {save.status === 'error' ? save.msg
                    : dirtyState === 'dirty' ? 'Unsaved — this browser only'
                      : save.status === 'saved' ? 'Saved as default'
                        : 'Matches saved default'}
                </div>
              </>
            )}
          </div>
        )}
        {!row && (
          <div style={{ ...mutedMono, whiteSpace: 'normal', lineHeight: 1.6, paddingTop: 4 }}>
            Select a node in the outliner to inspect it.
          </div>
        )}
        {row?.openSurface && (
          <button
            type="button"
            data-testid="shell-inspector-open-editor"
            onClick={() => onOpenSurface?.(row.openSurface)}
            style={{
              marginBottom: 6, padding: '6px 0', fontSize: 10.5, fontFamily: UI_FONT,
              ...pill, cursor: 'pointer',
              border: '1px solid var(--m-accent)', background: ACCENT_WASH,
              color: 'var(--m-accent)',
            }}
          >{row.openLabel ?? `Open ${row.label} editor`}</button>
        )}
        {row?.rigPreview && onOpenRigPreview && (
          <button
            type="button"
            data-testid="shell-inspector-open-rig-preview"
            onClick={onOpenRigPreview}
            title="Face controls for this splat character's rig"
            style={{
              marginBottom: 6, padding: '6px 0', fontSize: 10.5, fontFamily: UI_FONT,
              ...pill, cursor: 'pointer',
              border: '1px solid var(--m-accent)', background: ACCENT_WASH,
              color: 'var(--m-accent)',
            }}
          >Open Rig Preview</button>
        )}
        {/* Row-declared CONTROLS — the row states what it offers and this
            renders it, so a feature's properties live with the feature rather
            than in a floating panel this shell would have to know about. The
            navmesh's build params + Build button arrived here when the "Build
            Navmesh" window was retired. Unavailable controls are disabled with
            their reason, never hidden. */}
        {row?.controls?.map((c, i) => {
          const off = c.enabled === false
          if (c.kind === 'group') {
            return (
              <div key={`g${i}`} style={{
                ...mutedMono, fontSize: 9, letterSpacing: '.08em', textTransform: 'uppercase',
                color: 'var(--m-fg-3)', margin: '8px 0 2px',
              }}>{c.label}</div>
            )
          }
          if (c.kind === 'toggle') {
            return (
              <button
                key={c.id}
                type="button"
                data-testid={`shell-inspector-toggle-${c.id}`}
                data-on={c.value ? '1' : '0'}
                disabled={off}
                title={c.title}
                onClick={() => onRowControl?.(row.id, c.id, !c.value)}
                style={{
                  marginBottom: 4, padding: '6px 8px', fontSize: 10.5, fontFamily: UI_FONT,
                  ...pill, display: 'flex', alignItems: 'center', gap: 8,
                  cursor: off ? 'default' : 'pointer',
                  border: `1px solid ${c.value ? 'var(--m-accent)' : 'var(--m-line-hi)'}`,
                  background: c.value ? ACCENT_WASH : 'var(--m-panel-hi)',
                  color: off ? 'var(--m-fg-3)' : c.value ? 'var(--m-accent)' : 'var(--m-fg-2)',
                  opacity: off ? 0.6 : 1,
                }}
              >
                <span style={{
                  width: 22, height: 12, borderRadius: 999, flex: 'none', position: 'relative',
                  background: c.value ? 'var(--m-accent)' : 'var(--m-line-hi)',
                  transition: 'background 120ms var(--m-ease)',
                }}>
                  <span style={{
                    position: 'absolute', top: 2, left: c.value ? 12 : 2,
                    width: 8, height: 8, borderRadius: '50%', background: 'var(--m-panel)',
                    transition: 'left 120ms var(--m-ease)',
                  }} />
                </span>
                <span style={{ flex: 1, textAlign: 'left' }}>{c.label}</span>
              </button>
            )
          }
          if (c.kind === 'number') {
            return (
              <div key={c.id} title={c.title} style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '4px 7px', marginBottom: 4,
                background: 'var(--m-bg)', border: '1px solid var(--m-line)',
                borderRadius: 'var(--m-radius-sm)',
              }}>
                <span style={{ ...mutedMono, fontSize: 9.5, color: 'var(--m-fg-2)', flex: 1 }}>
                  {c.label}{c.unit ? ` (${c.unit})` : ''}
                </span>
                <input
                  type="number"
                  data-testid={`shell-inspector-number-${c.id}`}
                  value={c.value}
                  step={c.step ?? 0.05}
                  min={c.min ?? 0}
                  disabled={off}
                  onChange={e => {
                    const v = parseFloat(e.target.value)
                    if (Number.isFinite(v)) onRowControl?.(row.id, c.id, v)
                  }}
                  style={{
                    width: 62, padding: '2px 5px', textAlign: 'right',
                    fontFamily: MONO, fontSize: 9.5, color: 'var(--m-fg)',
                    background: 'var(--m-panel)', border: '1px solid var(--m-line-hi)',
                    borderRadius: 3,
                  }}
                />
              </div>
            )
          }
          if (c.kind === 'slider') {
            // A continuous 0..1 property, live: the handler writes the module
            // singleton the render loop reads, so a drag lands on the next frame.
            // Number-input siblings (the navmesh params) are discrete values you
            // type; this is one you dial while watching the thing it changes.
            return (
              <div key={c.id} style={{ marginBottom: 4 }}>
                <div title={c.title} style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '4px 7px',
                  background: 'var(--m-bg)', border: '1px solid var(--m-line)',
                  borderRadius: 'var(--m-radius-sm)',
                }}>
                  <span style={{ ...mutedMono, fontSize: 9.5, color: 'var(--m-fg-2)', flex: 'none', maxWidth: 118 }}>
                    {c.label}
                  </span>
                  <input
                    type="range"
                    data-testid={`shell-inspector-slider-${c.id}`}
                    min={c.min ?? 0}
                    max={c.max ?? 1}
                    step={c.step ?? 0.05}
                    value={c.value}
                    disabled={off}
                    onChange={e => {
                      const v = parseFloat(e.target.value)
                      if (Number.isFinite(v)) onRowControl?.(row.id, c.id, v)
                    }}
                    className="aos-slider"
                    style={{ flex: 1, minWidth: 0, '--aos-slider-fill': `${((c.value - (c.min ?? 0)) / ((c.max ?? 1) - (c.min ?? 0))) * 100}%` }}
                  />
                  <span
                    data-testid={`shell-inspector-slider-value-${c.id}`}
                    style={{
                      ...mutedMono, fontSize: 9.5, flex: 'none', width: 26, textAlign: 'right',
                      color: 'var(--m-fg)',
                    }}
                  >{c.format ? c.format(c.value) : c.value}</span>
                </div>
                {/* What this value MEANS at where it stands — the two ends are
                    different rigs, and naming them is what makes an intermediate
                    value legible rather than a mystery number. */}
                {c.note && (
                  <div data-testid={`shell-inspector-slider-note-${c.id}`} style={{
                    ...mutedMono, fontSize: 9, whiteSpace: 'normal', lineHeight: 1.5,
                    margin: '3px 0 0', color: 'var(--m-fg-3)',
                  }}>{c.note}</div>
                )}
                {/* A live measurement of what the control is doing, when it has
                    one — the answer to "is this slider even connected?". */}
                {c.live && (
                  <div data-testid={`shell-inspector-slider-live-${c.id}`} style={{
                    ...mutedMono, fontSize: 9, margin: '2px 0 0', color: 'var(--m-accent)',
                  }}>{c.live}</div>
                )}
              </div>
            )
          }
          if (c.kind === 'choice') {
            return (
              <div key={c.id} title={c.title} style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '4px 7px', marginBottom: 4,
                background: 'var(--m-bg)', border: '1px solid var(--m-line)',
                borderRadius: 'var(--m-radius-sm)',
              }}>
                <span style={{ ...mutedMono, fontSize: 9.5, color: 'var(--m-fg-2)', flex: 1 }}>{c.label}</span>
                <span style={{ display: 'inline-flex', border: '1px solid var(--m-line-hi)', borderRadius: 3, overflow: 'hidden' }}>
                  {c.options.map(([val, label]) => (
                    <button
                      key={val}
                      type="button"
                      data-testid={`shell-inspector-choice-${c.id}-${val}`}
                      disabled={off}
                      onClick={() => onRowControl?.(row.id, c.id, val)}
                      style={{
                        padding: '2px 8px', border: 0, fontFamily: UI_FONT, fontSize: 9.5,
                        cursor: off ? 'default' : 'pointer',
                        background: c.value === val ? 'var(--m-accent)' : 'transparent',
                        color: c.value === val ? 'var(--m-accent-ink)' : 'var(--m-fg-3)',
                        fontWeight: c.value === val ? 700 : 500,
                      }}
                    >{label}</button>
                  ))}
                </span>
              </div>
            )
          }
          if (c.kind === 'button') {
            return (
              <div key={c.id}>
                <button
                  type="button"
                  data-testid={`shell-inspector-action-${c.id}`}
                  disabled={off}
                  title={c.title}
                  onClick={() => onRowControl?.(row.id, c.id, true)}
                  style={{
                    // width: the button used to be a direct flex child and
                    // stretched; inside the wrapper div it shrink-wraps its
                    // label unless told otherwise (the "bleeding" pill).
                    width: '100%', boxSizing: 'border-box',
                    margin: '6px 0 0', padding: '6px 0', fontSize: 10.5, fontFamily: UI_FONT,
                    ...pill, cursor: off ? 'default' : 'pointer',
                    border: `1px solid ${off ? 'var(--m-line-hi)' : 'var(--m-accent)'}`,
                    background: off ? 'var(--m-panel-hi)' : ACCENT_WASH,
                    color: off ? 'var(--m-fg-3)' : 'var(--m-accent)',
                    opacity: off ? 0.6 : 1,
                  }}
                >{c.label}</button>
                {/* A disabled action must say WHY on screen — a hover-only title
                   reads as "mysteriously dead" (the Build Navmesh .spz case:
                   nobody discovers a tooltip on a control that does nothing). */}
                {off && c.title && (
                  <div data-testid={`shell-inspector-action-reason-${c.id}`} style={{
                    ...mutedMono, fontSize: 9, whiteSpace: 'normal', lineHeight: 1.5,
                    margin: '3px 0 6px', color: 'var(--m-fg-3)',
                  }}>{c.title}</div>
                )}
                {!off && <div style={{ height: 6 }} />}
              </div>
            )
          }
          return null
        })}
        {actionNote && (
          <div data-testid="shell-inspector-action-note" style={{
            ...mutedMono, fontSize: 9.5, whiteSpace: 'normal', lineHeight: 1.5, marginBottom: 6,
            color: actionNote.tone === 'error' ? 'var(--m-danger)' : 'var(--m-accent)',
          }}>{actionNote.text}</div>
        )}
        {row?.kv.map(([k, v]) => (
          <div
            key={k}
            style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '5px 7px',
              background: 'var(--m-bg)', border: '1px solid var(--m-line)',
              borderRadius: 'var(--m-radius-sm)',
            }}
          >
            <span style={{ ...mutedMono, fontSize: 9.5, color: 'var(--m-fg-2)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{k}</span>
            <span style={{ fontFamily: MONO, fontSize: 9.5, color: 'var(--m-fg)', whiteSpace: 'nowrap' }}>{v}</span>
          </div>
        ))}
      </div>

      </>
      )}
    </div>
  )
}

// ── Assets dock ───────────────────────────────────────────────────────────────

/**
 * The content browser, docked to the bottom of the viewport column and
 * collapsible to a 34px strip (⌥A, the header, or Assets ▸ "Assets dock" in
 * the menubar). The browser keeps its own search/filters/folder tree — this dock is
 * its window frame, not a second chrome.
 */
export function GeneratePanel() {
  const layout = useStore(shellLayout)
  const [active, setActive] = useState(generatePanelHost.isActive())
  useEffect(() => generatePanelHost.subscribe(() => setActive(generatePanelHost.isActive())), [])
  const hostRef = useCallback((el) => { generatePanelHost.registerTarget(el) }, [])

  // Existence is DERIVED from there being a generate session, so this region and
  // its resize handle appear together and leave together. No collapsed strip:
  // a permanent GENERATE rail with nothing behind it would be dead chrome, and
  // the panel already has its own ✕ / Cancel to close.
  //
  // Returning null before the host mounts is safe — ContentBrowser re-renders on
  // this store, so the portal lands as soon as the target exists.
  if (!active) return null

  return (
    <>
      <ResizeHandle axis="x" storeKey="generateW" invert />
      <div
        data-testid="shell-generate"
        style={{
          flex: 'none', width: layout.generateW, background: 'var(--m-panel)',
          borderLeft: LINE, display: 'flex', flexDirection: 'column', minWidth: 0,
        }}
      >
        {/* Portal target. The panel brings its own window chrome (FloatingWindow
            title bar with float / pop-out / close), so this region adds no
            header of its own — a second one would be the stacked-title-bar
            clutter FloatingWindow exists to remove. */}
        <div
          ref={hostRef}
          className="aos-thin-scroll"
          style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
        />
      </div>
    </>
  )
}

export function AssetsDock({ children }) {
  const layout = useStore(shellLayout)

  useEffect(() => {
    const onKey = (e) => {
      if (e.altKey && !e.ctrlKey && !e.metaKey && (e.code === 'KeyA')) {
        e.preventDefault()
        shellLayout.toggleDock()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  if (!layout.dockOpen) {
    return (
      <div
        data-testid="shell-dock"
        data-collapsed="1"
        onClick={() => shellLayout.toggleDock()}
        title="Expand assets (⌥A)"
        style={{
          flex: 'none', height: COLLAPSED_SIZE, background: 'var(--m-panel)',
          borderTop: '1px solid var(--m-line-hi)', cursor: 'pointer', userSelect: 'none',
          display: 'flex', alignItems: 'center', gap: 9, padding: '0 10px',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = 'color-mix(in srgb, var(--m-fg) 4%, var(--m-panel))' }}
        onMouseLeave={e => { e.currentTarget.style.background = 'var(--m-panel)' }}
      >
        <span style={label}>▴ ASSETS</span>
        <span style={{ flex: 1 }} />
        <span style={mutedMono}>⌥A</span>
      </div>
    )
  }

  return (
    <>
      <ResizeHandle axis="y" storeKey="dockH" invert />
      <div
        data-testid="shell-dock"
        data-collapsed="0"
        style={{
          flex: 'none', height: layout.dockH, background: 'var(--m-panel)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}
      >
        {/* NO header row of our own. The content browser already ships the row
            the build spec describes — label + add/delete/refresh + search +
            type filters + item count — so adding a second one stacked two
            "ASSETS" titles. It renders the collapse affordance on ITS label
            instead (ContentBrowser's `onCollapse`), leaving exactly one header. */}
        <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
          {children}
        </div>
      </div>
    </>
  )
}

// ── Console strip ─────────────────────────────────────────────────────────────

const WS_COLOR = {
  disconnected: 'var(--m-danger)',
  connecting: 'var(--m-warn)',
  connected: 'var(--m-online)',
}

/**
 * 26px status line. Counts are the REAL console tally (consoleTally wraps
 * console.error/warn), the dot is the live Convorcher WS state, and the
 * right-hand text is the active character readout.
 */
export function ConsoleStrip({ wsState = 'disconnected', statusText = '', children }) {
  const [tally, setTally] = useState(consoleTally.get())
  useEffect(() => consoleTally.subscribe(setTally), [])

  return (
    <div
      data-testid="shell-console-strip"
      style={{
        flex: 'none', display: 'flex', alignItems: 'center', gap: 14,
        padding: '0 12px', height: 26, borderTop: LINE, background: 'var(--m-panel)',
        fontFamily: MONO, fontSize: 9, color: 'var(--m-fg-3)',
        whiteSpace: 'nowrap', overflow: 'hidden', userSelect: 'none',
      }}
    >
      <span style={{ letterSpacing: 'var(--m-head-ls)', color: 'var(--m-fg-2)' }}>CONSOLE</span>
      <span data-testid="shell-console-errors" style={{ color: tally.errors ? 'var(--m-danger)' : 'var(--m-fg-3)' }}>
        {tally.errors} {tally.errors === 1 ? 'error' : 'errors'}
      </span>
      <span style={{ color: tally.warnings ? 'var(--m-warn)' : 'var(--m-fg-3)' }}>
        {tally.warnings} {tally.warnings === 1 ? 'warning' : 'warnings'}
      </span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ width: 5, height: 5, borderRadius: '50%', background: WS_COLOR[wsState] ?? 'var(--m-fg-3)' }} />
        convorcher {wsState}
      </span>
      <span style={{ flex: 1 }} />
      <span data-testid="shell-console-status">{statusText}</span>
      {children}
    </div>
  )
}

export { ResizeHandle }
